import React, { useEffect, useMemo, useState } from 'react';
import { demeterApi } from '../../../../Apis/Apis';
import scssVariables from '../../../../Config.module.scss';
import { MarketPricesTimeSpan, UnitOfMeasure } from '../../../../Generated/Raven';
import {
    CommoditySpotPriceModel,
    Currency,
    DemeterFilterTimeSpan,
    GetMarketIndicatorFactorDataResponse,
    GetMarketIndicatorResponse,
    MarketIndicatorFactorDataModel,
    MarketIndicatorOutlook,
    MarketIndicatorOutlookResultModel,
    MarketIndicatorTemplateType,
    MarketPriceModel,
    RunMarketIndicatorFactorResponse,
    RunMarketIndicatorResponse,
} from '../../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import useSymbolsApi from '../../../Apis/Hooks/useSymbolsApiHook';
import { yearsOfDataOptions } from '../../../Pages/Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import { chartColors, IChartAreaRangeData, IChartAreaRangeDataSeries, IChartBarDataSeries, IChartData, IChartDataSeries } from '../ChartDefinitions';
import chartService from '../ChartService';
import ChartWrapper from '../ChartWrapper/ChartWrapper';
import marketIndicatorChartService from '../MarketIndicators/MarketIndicatorChartService';
import ProjectionChartRaw from '../Projection/ProjectionChartRaw';
import MarketIndicatorChartRaw, { PlotBand } from './MarketIndicatorChartRaw';

export interface IMarketIndicatorChartProps {
    title?: string;
    customTitle?: string | JSX.Element;
    marketIndicatorOutlook?: MarketIndicatorOutlook;
    runMarketIndicatorResponse?: RunMarketIndicatorResponse | undefined;
    runTestMarketIndicatorFactorResponse?: RunMarketIndicatorFactorResponse | GetMarketIndicatorFactorDataResponse;
    secondaryRunTestMarketIndicatorFactorResponse?: RunMarketIndicatorFactorResponse | GetMarketIndicatorFactorDataResponse;
    useProjectionChart?: boolean;
    yAxisLabel?: string;
    isLoading?: boolean;
    ignorePlotbands?: boolean;
    showOnlyAsPopout?: boolean;
    showPopout?: boolean;
    setShowPopout?: (showPopout: boolean) => void;
    useHedgeMonitorProRows?: boolean;
    useColorPalletteInLegend?: boolean;
    displayDecimalPlacesMinimum?: number;
    displayDecimalPlacesMaximum?: number;
    chartNavigatorTimeRange?: DemeterFilterTimeSpan;
    testId?: string;
    track: TrackingEvent;
}

const defaultContractNumber = 1;
const defaultMarketPricesTimeSpan = MarketPricesTimeSpan.FourYears;

const MarketIndicatorChart: React.FC<IMarketIndicatorChartProps> = (props: IMarketIndicatorChartProps) => {
    // Application hooks.
    const [translations, translate] = useLanguage();

    // Data hooks.
    const getMarketIndicatorFactorResponse = props.runTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse;
    const additionalData = getMarketIndicatorFactorResponse?.additionalData;
    const symbols = useSymbolsApi();
    const symbolModel = useMemo(
        () =>
            symbols?.find(
                (x) =>
                    x.commodity === (getMarketIndicatorFactorResponse?.commodity ?? props.runMarketIndicatorResponse?.marketIndicator?.commodity) &&
                    x.region === (getMarketIndicatorFactorResponse?.region ?? props.runMarketIndicatorResponse?.marketIndicator?.region),
            ),
        [symbols, props.runMarketIndicatorResponse, props.runTestMarketIndicatorFactorResponse],
    );

    const [, refreshListMarketPrices, listMarketPricesResponse] = useApiWithoutAutoExecute(() =>
        demeterApi.listMarketPricesRollingWithConversions(
            symbolModel?.reutersInstrumentCodePrefix ?? '',
            props.runMarketIndicatorResponse?.marketIndicator?.contractNumber ?? defaultContractNumber,
            yearsOfDataOptions.find((x) => x.value === `${props.runMarketIndicatorResponse?.marketIndicator?.numberOfYears}`)?.marketPricesTimeSpan ??
                defaultMarketPricesTimeSpan,
        ),
    );

    const [, refreshListBasisPrices, listBasisPricesResponse] = useApiWithoutAutoExecute(() => {
        if (!props.runMarketIndicatorResponse?.marketIndicator?.usePrices) {
            return null;
        }

        return demeterApi.listCommodityBasisPrices(
            props.runMarketIndicatorResponse.marketIndicator.region,
            props.runMarketIndicatorResponse.marketIndicator.commodity,
            props.runMarketIndicatorResponse.marketIndicator.dataSource,
            undefined,
            props.runMarketIndicatorResponse.marketIndicator.subRegion,
            undefined,
            defaultMarketPricesTimeSpan,
        );
    });

    // Chart hooks.
    const [lineSeries, setLineSeries] = useState<IChartDataSeries[]>([]);
    const [areaRangeSeries, setAreaRangeSeries] = useState<IChartAreaRangeDataSeries[]>([]);

    const chartTitle = useMemo(() => {
        if (props.title) {
            return props.title;
        }

        if (!getMarketIndicatorFactorResponse && !props.runMarketIndicatorResponse) {
            return '';
        }

        return getMarketIndicatorFactorResponse?.factorDisplayName ?? props.runMarketIndicatorResponse?.marketIndicator?.displayName ?? '';
    }, [symbolModel, props.runMarketIndicatorResponse, props.runTestMarketIndicatorFactorResponse]);

    const primaryLineSeriesLabel = useMemo(() => {
        if (symbolModel) {
            return formattingService.toDisplayName(symbolModel);
        }

        if (!getMarketIndicatorFactorResponse && !props.runMarketIndicatorResponse) {
            return '';
        }

        return getMarketIndicatorFactorResponse?.commodityDisplayName ?? props.runMarketIndicatorResponse?.marketIndicator?.displayName ?? '';
    }, [symbolModel, getMarketIndicatorFactorResponse, props.runMarketIndicatorResponse]);

    const secondaryLineSeriesLabel = useMemo(() => {
        const secondaryResponse = props.secondaryRunTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse;

        if (secondaryResponse?.templateType === MarketIndicatorTemplateType.Technical) {
            return secondaryResponse.factorDisplayName!;
        }

        if (props.secondaryRunTestMarketIndicatorFactorResponse && secondaryResponse?.additionalData?.length) {
            const primaryLabel = secondaryResponse?.additionalData?.find((x) => x.name === 'PrimaryData')?.displayName;
            const secondaryLabel = secondaryResponse?.additionalData?.find((x) => x.name === 'SecondaryData')?.displayName;

            return `${primaryLabel} ${
                secondaryResponse.dataCombinationType ? translations.operatorSymbol[secondaryResponse.dataCombinationType] : ''
            } ${secondaryLabel}`;
        }

        if (props.secondaryRunTestMarketIndicatorFactorResponse && secondaryResponse?.commodityDisplayName) {
            return secondaryResponse.commodityDisplayName ?? '';
        }

        if (symbolModel) {
            return formattingService.toDisplayName(symbolModel);
        }

        if (!getMarketIndicatorFactorResponse && !props.runMarketIndicatorResponse) {
            return '';
        }

        return getMarketIndicatorFactorResponse?.commodityDisplayName ?? props.runMarketIndicatorResponse?.marketIndicator?.displayName ?? '';
    }, [
        symbolModel,
        getMarketIndicatorFactorResponse,
        props.runMarketIndicatorResponse,
        props.runTestMarketIndicatorFactorResponse,
        props.secondaryRunTestMarketIndicatorFactorResponse,
    ]);

    const dataSourceTag = useMemo(() => {
        const sourceTags = [];

        const runTestResponse = props.runTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse;

        if (runTestResponse && runTestResponse.dataSourceTags && runTestResponse.dataSourceTags.length > 0) {
            sourceTags.push(runTestResponse.dataSourceTags[0]);
        }

        if (listBasisPricesResponse?.dataSourceTag) {
            sourceTags.push(listBasisPricesResponse?.dataSourceTag);
        }

        if (!props.runMarketIndicatorResponse?.marketIndicator?.usePrices) {
            sourceTags.push(symbolModel?.exchange ?? '', translations.dataSource.StoneXCalculations);
        }

        return sourceTags;
    }, [symbolModel, listBasisPricesResponse]);

    // The footer only shows the first data source tag, so the restriction has to be checked against all of them.
    const disableTooltip = chartService.isRestrictedDataSource(
        ...dataSourceTag,
        ...(getMarketIndicatorFactorResponse?.dataSourceTags ?? []),
        ...((props.secondaryRunTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse)?.dataSourceTags ?? []),
    );

    const isIndicatorPerformanceWithPlotBands = useMemo(
        () => !props.useHedgeMonitorProRows && !props.ignorePlotbands,
        [props.useHedgeMonitorProRows, props.ignorePlotbands],
    );

    useEffect(() => {
        if (!props.runMarketIndicatorResponse || !symbols || props.ignorePlotbands) {
            return;
        }

        if (props.runMarketIndicatorResponse.marketIndicator?.usePrices) {
            refreshListBasisPrices();
        } else {
            refreshListMarketPrices();
        }
    }, [props.runMarketIndicatorResponse, symbols, props.runTestMarketIndicatorFactorResponse]);

    useEffect(() => {
        const usePrices = props.runMarketIndicatorResponse?.marketIndicator?.usePrices;
        const isRunTestMarketIndicatorFactorData = props.ignorePlotbands && props.runTestMarketIndicatorFactorResponse;

        if (
            ((!usePrices && (!listMarketPricesResponse?.rows || listMarketPricesResponse?.rows.length === 0)) ||
                (usePrices && (!listBasisPricesResponse?.rows || listBasisPricesResponse?.rows.length === 0))) &&
            !isRunTestMarketIndicatorFactorData
        ) {
            setLineSeries([]);
            setAreaRangeSeries([]);
            return;
        }

        let lineSeriesData: IChartData[] = [];

        if (isRunTestMarketIndicatorFactorData) {
            lineSeriesData = getLineSeries((props.runTestMarketIndicatorFactorResponse?.rows as MarketIndicatorFactorDataModel[]) ?? []);
            // TODO - These two below have slightly different patterns. When we create the functions for the chartService,
            // we can update these functions in there.
        } else if (usePrices) {
            listBasisPricesResponse!.rows!.forEach((row: CommoditySpotPriceModel) => {
                lineSeriesData.push({
                    value: row.value ?? 0,
                    asOfDate: new Date(row.asOfDate),
                    isActualValue: true,
                });
            });
        } else {
            listMarketPricesResponse!.rows!.forEach((row: MarketPriceModel) => {
                lineSeriesData.push({
                    value: row.settlementPrice,
                    asOfDate: new Date(row.asOfDate),
                    isActualValue: true,
                });
            });
        }

        let lineSeriesTitle = primaryLineSeriesLabel;
        if (
            getMarketIndicatorFactorResponse?.templateType === MarketIndicatorTemplateType.ForwardCurve ||
            getMarketIndicatorFactorResponse?.templateType === MarketIndicatorTemplateType.Speculative
        ) {
            lineSeriesTitle = getMarketIndicatorFactorResponse?.commodityDisplayName ?? chartTitle;
        }

        const newLinesSeries: IChartDataSeries[] = [
            {
                label: lineSeriesTitle,
                data: lineSeriesData,
                axisLabel: props.yAxisLabel,
            },
        ];

        const newAreasSeries: IChartAreaRangeDataSeries[] = [];

        if (additionalData) {
            const primaryData = additionalData?.find((x) => x.name === 'PrimaryData') ?? additionalData?.find((x) => x.name === 'ShortTermValue');
            const longTermValue = additionalData?.find((x) => x.name === 'LongTermValue');
            const secondaryData = additionalData?.find((x) => x.name === 'SecondaryData') ?? longTermValue;
            const longPosition = additionalData?.find((x) => x.name === 'LongPosition');
            const shortPosition = additionalData?.find((x) => x.name === 'ShortPosition');
            const averageForecast = additionalData?.find((x) => x.name === 'AverageForecast');

            const primaryLineDataSeries = getLineSeries(primaryData?.rows ?? []);
            const secondaryLineDataSeries = getLineSeries(secondaryData?.rows ?? []);
            const averageForecastDataSeries = getLineSeries(averageForecast?.rows ?? []);
            const longPositionDataSeries = getAreaRangeSeries(longPosition?.rows ?? []);
            const shortPositionDataSeries = getAreaRangeSeries(shortPosition?.rows ?? [], true);

            const netPosition =
                longPosition?.rows?.map((longItem, index) => {
                    const shortItem = shortPosition && shortPosition!.rows ? shortPosition!.rows[index] : null;
                    const value = longItem.value - (shortItem?.value ?? 0);

                    return {
                        value: longItem.value - (shortItem?.value ?? 0),
                        asOfDate: new Date(longItem.asOfDate),
                        isActualValue: longItem.isActualValue,
                    } as IChartData;
                }) ?? [];

            if (primaryLineDataSeries.length > 0) {
                newLinesSeries.push(
                    {
                        label: `${translate(primaryData?.displayName ?? '')}`,
                        data: primaryLineDataSeries,
                    },
                    {
                        label: `${translate(secondaryData?.displayName ?? '')}`,
                        data: secondaryLineDataSeries,
                        yAxis: longTermValue ? 0 : 1,
                    } as IChartDataSeries,
                );
            }

            if (longPositionDataSeries.length > 0) {
                newLinesSeries.push({
                    label: translations.text.netPosition,
                    data: netPosition,
                    yAxis: 1,
                });
            }

            if (averageForecastDataSeries.length > 0) {
                newLinesSeries.push({
                    label: `${translate(averageForecast?.displayName ?? '')}`,
                    data: averageForecastDataSeries,
                });
            }
        }

        const secondaryChartData = marketIndicatorChartService.convertToChartData(
            (props.secondaryRunTestMarketIndicatorFactorResponse?.rows as MarketIndicatorFactorDataModel[]) ?? [],
        );
        const lineValuesAreDistinct = !secondaryChartData.every((x, index) => x.value === newLinesSeries[0].data[index]?.value);

        if (secondaryChartData.length > 0 && lineValuesAreDistinct) {
            newLinesSeries.push({
                label: secondaryLineSeriesLabel,
                data: secondaryChartData,
                yAxis: 1,
                color: chartColors.lineChartColorsRuleSetOne[3],
                // isPrimaryLine is used to get the desired lineWidth since both lines are equal importance.
                isPrimaryLine: true,
            });
        }

        setLineSeries(newLinesSeries);

        if (newAreasSeries.length > 0) {
            setAreaRangeSeries(newAreasSeries);
        } else {
            setAreaRangeSeries([]);
        }
    }, [
        props.ignorePlotbands,
        listMarketPricesResponse,
        listBasisPricesResponse,
        props.runTestMarketIndicatorFactorResponse,
        props.secondaryRunTestMarketIndicatorFactorResponse,
    ]);

    const getLineSeries = (rows: MarketIndicatorFactorDataModel[]) => {
        const currentLineValues: IChartData[] = [];

        rows.forEach((row) => {
            if (!row.value) {
                return;
            }

            currentLineValues.push({
                value: row.value,
                asOfDate: new Date(row.asOfDate),
                isActualValue: row.isActualValue,
            });
        });

        return currentLineValues;
    };

    const getAreaRangeSeries = (rows: MarketIndicatorFactorDataModel[], invertMinimumValue?: boolean) => {
        const currentAreaRangeValues: IChartAreaRangeData[] = [];

        rows.forEach((row) => {
            if (!row.value) {
                return;
            }

            currentAreaRangeValues.push({
                value: 0,
                maximumValue: invertMinimumValue ? 0 : row.value,
                minimumValue: invertMinimumValue ? -row.value : 0,
                asOfDate: new Date(row.asOfDate),
                isActualValue: true,
            });
        });

        return currentAreaRangeValues;
    };

    const getCurrencyOrUnitOfMeasureFromResponseData = (key: 'currency' | 'unitOfMeasure') => {
        if (getMarketIndicatorFactorResponse && getMarketIndicatorFactorResponse[key]) {
            return getMarketIndicatorFactorResponse[key];
        }

        if (listMarketPricesResponse && listMarketPricesResponse[key]) {
            return listMarketPricesResponse[key];
        }

        if (listBasisPricesResponse && listBasisPricesResponse[key]) {
            return listBasisPricesResponse[key];
        }

        if (additionalData?.length && additionalData[0][key]) {
            return additionalData[0][key];
        }

        return '';
    };

    const plotBands = useMemo(() => {
        if (!props.runTestMarketIndicatorFactorResponse?.rows && !props.runMarketIndicatorResponse?.rows) {
            return [];
        }

        const newBands: PlotBand[] = [];
        const marketDataByDates = props.useHedgeMonitorProRows
            ? (props.runMarketIndicatorResponse as GetMarketIndicatorResponse)?.hedgeMonitorProRows
            : props.runTestMarketIndicatorFactorResponse?.rows ?? props.runMarketIndicatorResponse?.rows;
        const filteredDataByDates = (marketDataByDates as MarketIndicatorOutlookResultModel[])?.filter((x) => x.outlook !== MarketIndicatorOutlook.Unavailable);
        // TODO: I'm not sure if it's right, need to check logic here.
        filteredDataByDates?.forEach((x, index, array) => {
            let toDate = new Date((filteredDataByDates[index + 1] ?? x).asOfDate).getTime();
            if (index === array.length - 1) {
                toDate = new Date().getTime();
            }
            if (index === 0 || filteredDataByDates[index - 1].outlook !== x.outlook) {
                newBands.push({
                    fromDate: new Date(x.asOfDate).getTime(),
                    toDate,
                    color: chartService.getOutlookColor(x.outlook, true, isIndicatorPerformanceWithPlotBands),
                });
            } else {
                newBands[newBands.length - 1].toDate = toDate;
            }
        });

        return newBands;
    }, [props.runTestMarketIndicatorFactorResponse, props.runMarketIndicatorResponse]);

    const isLoading = useMemo(
        () =>
            props.isLoading ||
            (!props.runMarketIndicatorResponse && !props.runTestMarketIndicatorFactorResponse) ||
            (!props.runTestMarketIndicatorFactorResponse && props.ignorePlotbands) ||
            lineSeries.length === 0,
        [props.isLoading, props.runMarketIndicatorResponse, props.runTestMarketIndicatorFactorResponse, props.ignorePlotbands, lineSeries],
    );

    const yAxisLabels = useMemo(() => {
        if (
            !additionalData ||
            additionalData.length < 2 ||
            (!additionalData[1].currency && !additionalData[1].unitOfMeasure) ||
            additionalData.find((x) => x.name === 'ShortTermValue') ||
            !getMarketIndicatorFactorResponse
        ) {
            return undefined;
        }

        return [
            `${getMarketIndicatorFactorResponse.commodityDisplayName} ${chartService.getCurrencyAndUnitOfMeasureText(
                getMarketIndicatorFactorResponse.unitOfMeasure ?? additionalData[0].unitOfMeasure,
                getMarketIndicatorFactorResponse.currency ?? additionalData[0].currency,
            )}`,
            `${getMarketIndicatorFactorResponse.secondaryCommodityDisplayName} ${chartService.getCurrencyAndUnitOfMeasureText(
                getMarketIndicatorFactorResponse.secondaryUnitOfMeasure ?? additionalData[1].unitOfMeasure,
                getMarketIndicatorFactorResponse.secondaryCurrency ?? additionalData[1].currency,
            )}`,
        ];
    }, [props.runTestMarketIndicatorFactorResponse, additionalData, translations, getMarketIndicatorFactorResponse]);

    const currency = useMemo(
        () => getCurrencyOrUnitOfMeasureFromResponseData('currency') as Currency,
        [listMarketPricesResponse, listBasisPricesResponse, getMarketIndicatorFactorResponse],
    );

    const unitOfMeasure = useMemo(
        () => getCurrencyOrUnitOfMeasureFromResponseData('unitOfMeasure') as UnitOfMeasure,
        [listMarketPricesResponse, listBasisPricesResponse, getMarketIndicatorFactorResponse],
    );

    const secondaryCurrency = useMemo(() => {
        if (getMarketIndicatorFactorResponse?.secondaryCurrency) {
            return getMarketIndicatorFactorResponse.secondaryCurrency;
        }

        const secondaryDataCurrency = additionalData?.find((x) => x.name === 'SecondaryData')?.currency;
        if (secondaryDataCurrency) {
            return secondaryDataCurrency;
        }

        const secondaryResponse = props.secondaryRunTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse;
        if (secondaryResponse?.currency) {
            return secondaryResponse.currency;
        }

        return undefined;
    }, [getMarketIndicatorFactorResponse, additionalData]);

    const secondaryUnitOfMeasure = useMemo(() => {
        if (getMarketIndicatorFactorResponse?.secondaryUnitOfMeasure) {
            return getMarketIndicatorFactorResponse.secondaryUnitOfMeasure;
        }

        const secondaryDataUnitOfMeasure = additionalData?.find((x) => x.name === 'SecondaryData')?.unitOfMeasure;
        if (secondaryDataUnitOfMeasure) {
            return secondaryDataUnitOfMeasure;
        }

        const secondaryResponse = props.secondaryRunTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse;
        if (secondaryResponse?.unitOfMeasure) {
            return secondaryResponse.unitOfMeasure;
        }

        return undefined;
    }, [getMarketIndicatorFactorResponse, additionalData]);

    if (props.useProjectionChart) {
        const oldestDate = new Date();
        oldestDate.setFullYear(oldestDate.getFullYear() - (props.runMarketIndicatorResponse?.marketIndicator?.numberOfYears ?? 0));

        const projectionChartSeries: IChartDataSeries[] = lineSeries.map((x) => ({
            ...x,
            data: x.data.filter((y) => y.asOfDate.getTime() >= oldestDate.getTime()),
        }));

        const areaColor = scssVariables.chartColor17WithOpacity;
        const projectionChartLineSeries: IChartDataSeries[] = [{ ...projectionChartSeries[0], color: chartColors.lineChartColorsRuleSetTwo[0] }];
        const projectionChartBarSeries = { ...{ ...(projectionChartSeries[1] ?? []), color: areaColor, appearAsArea: true } } as IChartBarDataSeries;
        const numberOfLineDecimalPlacesValue = formattingService.getDisplayDecimalPlacesMinimumForCharts(
            Math.min(...(projectionChartLineSeries[0].data ?? []).map((x) => Math.abs(x.value)).filter((x) => !!x)),
        );

        projectionChartLineSeries[0].displayDecimalPlacesMinimum = numberOfLineDecimalPlacesValue;
        projectionChartLineSeries[0].displayDecimalPlacesMaximum = numberOfLineDecimalPlacesValue;

        return (
            <ChartWrapper
                headerOptions={{
                    showOnlyAsPopout: props.showOnlyAsPopout,
                }}
                showPopout={props.showPopout}
                setShowPopout={props.setShowPopout}
                name="MarketIndicatorChart"
                title={chartTitle}
                customTitle={props.customTitle}
                marketIndicatorOutlook={props.marketIndicatorOutlook}
                dataSourceTag={dataSourceTag}
                isLoading={isLoading || !lineSeries.length}
                testId={props.testId || 'MarketIndicatorFactorChart'}
                track={props.track}
            >
                <ProjectionChartRaw
                    linesSeries={projectionChartLineSeries}
                    barSeries={projectionChartBarSeries}
                    unitOfMeasure={unitOfMeasure}
                    currency={currency}
                    dataFrequency={(props.runTestMarketIndicatorFactorResponse as GetMarketIndicatorFactorDataResponse)?.dataFrequency}
                    turboThreshold={0}
                    barSeriesScreenPercent={20}
                    disableTooltip={disableTooltip}
                />
            </ChartWrapper>
        );
    }

    // If in popout only mode, ignore the skeleton.
    return (
        <ChartWrapper
            headerOptions={{
                showOnlyAsPopout: props.showOnlyAsPopout,
            }}
            showPopout={props.showPopout}
            setShowPopout={props.setShowPopout}
            name="MarketIndicatorChart"
            title={chartTitle}
            customTitle={props.customTitle}
            marketIndicatorOutlook={props.marketIndicatorOutlook}
            dataSourceTag={dataSourceTag}
            isLoading={isLoading}
            testId={props.testId || 'MarketIndicatorFactorChart'}
            track={props.track}
        >
            <MarketIndicatorChartRaw
                lineSeries={lineSeries}
                areaRangeSeries={areaRangeSeries}
                plotBands={props.ignorePlotbands || !plotBands ? [] : plotBands}
                currencies={secondaryCurrency ? [currency, secondaryCurrency] : [currency]}
                unitOfMeasures={secondaryUnitOfMeasure ? [unitOfMeasure, secondaryUnitOfMeasure] : [unitOfMeasure]}
                yAxisLabelPrimary={yAxisLabels?.[0]}
                yAxisLabelSecondary={yAxisLabels?.[1]}
                useColorPalletteInLegend={props.useColorPalletteInLegend}
                displayDecimalPlacesMinimum={props.displayDecimalPlacesMinimum}
                displayDecimalPlacesMaximum={props.displayDecimalPlacesMaximum}
                isIndicatorTechnicalPerformance={isIndicatorPerformanceWithPlotBands}
                navigatorTimeRange={props.chartNavigatorTimeRange}
                disableTooltip={disableTooltip}
            />
        </ChartWrapper>
    );
};

export default MarketIndicatorChart;
